import { FunnelIcon } from "@heroicons/react/24/outline";
import { cva } from "class-variance-authority";
import moment from "moment";
import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { myWorksDocumentsListName } from "src/api/config";
import { useAppSelector } from "src/hooks/redux";
import { selectGetStatus } from "src/modules/spItems/selectors";
import { ApproverList, CodeStatus, MyWorkDocumentsType } from "src/types/main";
import { TooltipItem } from "src/utils/Tooltip";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";
import { TableColumn, TableColumnWithIcon, TableHeaderContentType } from "../../pages/DashboardPage/acadia/mock";
import { EmptyList } from "../EmptyList";
import Loading from "../Loading";
import SortIcon from "../ui-icons/table/SortIcon";
import { DraftIcon, OnApprovalIcon, OnReviewIcon, OnRevisionIcon, RejectedIcon } from "../ui-icons/table/codeStatus";
import { UserAvatarItem } from "../ui/TGUserAvatarsList";
import { TableHeaderColumnProps } from "../ui/table/Table";

const tableHeaderContent: Record<keyof TableHeaderContentType, TableColumnWithIcon> = {
  status: {
    icon: <FunnelIcon width={16} height={16} color={"#9CA3AF"} className="mt-1 cursor-pointer" />,
    sort: false,
    style: { width: "3%" },
  },
  code: {
    title: "Код",
    sort: true,
    style: { width: "16%" },
  },
  name: {
    title: "Название документа",
    sort: true,
    style: { width: "20%" },
  },
  collection: {
    title: "Коллекция",
    sort: true,
    style: { width: "12%" },
  },
  department: {
    title: "Подразделение/\u00A0Функция",
    sort: true,
    style: { width: "17%" },
  },
  type: {
    title: "Тип",
    sort: true,
    style: { width: "8%" },
  },
  sentForApproval: {
    title: "Отправлено на\u00A0согласование",
    sort: true,
    style: { width: "13%" },
  },
  approversList: {
    title: "Список утверждающих",
    sort: false,
    style: { width: "11%" },
  },
};

function WorksDocumentTable({ documents }: { documents: MyWorkDocumentsType[] }) {
  return <Table tableName="Table with Work Documents" header={tableHeaderContent} body={documents} />;
}

export default WorksDocumentTable;

const ON_REVIEW_STATUS_ID = 3;
const SENT_FOR_APPROVAL_DATE_FORMAT = "DD.MM.YYYY";

function getSentForApprovalSortTimestamp(item: MyWorkDocumentsType): number | null {
  if (item.status.id === ON_REVIEW_STATUS_ID) {
    return null;
  }

  const parsed = moment(item.sentForApproval, SENT_FOR_APPROVAL_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.valueOf() : null;
}

function compareSentForApproval(
  a: MyWorkDocumentsType,
  b: MyWorkDocumentsType,
  sortDirection: "asc" | "desc",
): number {
  const timestampA = getSentForApprovalSortTimestamp(a);
  const timestampB = getSentForApprovalSortTimestamp(b);

  if (timestampA === null && timestampB === null) {
    return 0;
  }
  if (timestampA === null) {
    return 1;
  }
  if (timestampB === null) {
    return -1;
  }

  const diff = timestampA - timestampB;
  return sortDirection === "asc" ? diff : -diff;
}

const statusesMap = new Map<number, { title: string; id: number }>();

function Table({
  header,
  body,
  tableName,
}: {
  header: Record<keyof TableHeaderContentType, TableColumn>;
  body: MyWorkDocumentsType[];
  tableName: string;
}) {
  const sortIndex = useRef(0);
  const [sortedField, setSortedField] = useState<keyof TableHeaderContentType | null>("sentForApproval");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("asc");
  const [sortStatus, setSortStatus] = useState<number>(0);

  const handleSort = (field: keyof TableHeaderContentType) => {
    if (sortedField === field) {
      if (sortedField === "status") {
        if (sortIndex.current === statusesMap.size) {
          sortIndex.current = 0;
          setSortStatus(0);
        } else {
          const statusValues: { title: string; id: number }[] = [...statusesMap.values()];
          const id = statusValues[sortIndex.current].id;

          sortIndex.current = sortIndex.current + 1;
          setSortStatus(id);
        }
      } else {
        setSortDirection((prevDirection) =>
          prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc",
        );
      }
    } else {
      setSortedField(field);
      setSortDirection("asc");

      if (field === "status") {
        const statusValues: { title: string; id: number }[] = [...statusesMap.values()];
        setSortStatus(statusValues[0].id);
        sortIndex.current = 1;
      } else {
        setSortStatus(0);
        sortIndex.current = 0;
      }
    }
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!sortedField || sortDirection === "default") {
      return body;
    }

    const sortedData = [...body];

    sortedData.sort((a, b) => {
      if (!a.isReplaced && b.isReplaced) {
        return 1;
      }
      if (a.isReplaced && !b.isReplaced) {
        return -1;
      }

      if (sortedField === "status") {
        if (a[sortedField].id === sortStatus && b[sortedField].id !== sortStatus) {
          return -1;
        }
        if (b[sortedField].id === sortStatus && a[sortedField].id !== sortStatus) {
          return 1;
        }
        return 0;
      }

      if (sortedField === "sentForApproval") {
        return compareSentForApproval(a, b, sortDirection);
      }

      const valueA = a[sortedField];
      const valueB = b[sortedField];

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return sortDirection === "asc" ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }

      return 0;
    });

    return sortedData;
  }, [sortedField, sortDirection, body, sortStatus]) as MyWorkDocumentsType[];

  return (
    <FlexRow>
      <FlexCol className="w-full">
        <TableList
          head={header}
          body={sortedTableBodyContent}
          sort={handleSort}
          sortDirection={sortDirection}
          sortedField={sortedField}
          tableName={tableName}
          sortStatus={sortStatus}
        />
      </FlexCol>
    </FlexRow>
  );
}

function TableList({
  head,
  body,
  sort,
  sortDirection,
  sortedField,
  tableName,
  sortStatus,
}: {
  head: Record<keyof TableHeaderContentType, TableColumnWithIcon>;
  body: MyWorkDocumentsType[];
  sort: (f: keyof TableHeaderContentType) => void;
  sortedField: keyof TableHeaderContentType | null;
  sortDirection: "asc" | "desc" | "default";
  tableName: string;
  sortStatus: number;
}) {
  const [query, setQuery] = useSearchParams();
  let status = useAppSelector((state) => selectGetStatus(state, myWorksDocumentsListName));

  function openDocWithVersionCard(id: number, versionId: number) {
    const newSearchParams = new URLSearchParams(query);

    if (!!versionId) {
      newSearchParams.set("documentCard", versionId.toString());
      newSearchParams.set("version", id.toString());
    } else {
      newSearchParams.set("documentCard", id.toString());
    }

    setQuery(newSearchParams);
  }

  const tableFields = useMemo(() => {
    return Object.keys(head);
  }, [head]);

  return (
    <FlexRow className="px-6 py-5 overflow-x-auto bg-white rounded-lg table-custom shadow-custom scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-thumb-[#D1D5DB] max-w-full">
      <FlexCol className="min-w-[1235px] grow min-h-[300px] max-h-[calc(100vh-330px)] overflow-y-auto thin-scrollbar">
        <FlexRow className="sticky top-0 z-10 gap-x-4 px-3 pb-5 bg-white">
          {tableFields.map((fieldKey) => {
            const key = fieldKey as keyof TableHeaderContentType;
            return (
              <TableHeaderColumn
                key={key}
                tableHeaderContent={head[key]}
                sortStatus={sortStatus}
                sortDirection={sortedField === key ? sortDirection : "default"}
                onClick={() => sort(key)}
                onStatusClickIcon={() => sort("status")}
              />
            );
          })}
        </FlexRow>
        <FlexCol className="gap-y-1 h-full table-wrap">
          {status !== "SUCCESS" ? (
            <div className="flex justify-center items-center pt-4 h-full">
              <Loading />
            </div>
          ) : Array.isArray(body) && body.length > 0 ? (
            body.map((item) => {
              statusesMap.set(item.status.id, item.status);

              return (
                <FlexRow
                  className="py-4 hover:bg-[#F3F4F6] hover:bg-opacity-50 rounded-lg cursor-pointer gap-x-4 px-3 items-center"
                  key={item.id}
                  onClick={() => openDocWithVersionCard(item.id, item.versionId)}
                  style={{ backgroundColor: item.isReplaced ? "#FEF3C7" : "white" }}
                >
                  <FlexCol className="break-words text-[#637381]" style={{ width: "3%" }}>
                    <StatusListItem status={item.status} />
                  </FlexCol>
                  <FlexCol className="break-words text-[#637381]" style={{ width: "16%" }}>
                    {item.code}
                  </FlexCol>
                  <FlexCol className="break-words text-darkblue" style={{ width: "20%" }}>
                    {item.name}
                  </FlexCol>
                  <FlexCol className="break-words text-[#637381]" style={{ width: "12%" }}>
                    {item.collection}
                  </FlexCol>
                  <FlexCol className="break-words text-[#637381]" style={{ width: "17%" }}>
                    {item.department}
                  </FlexCol>
                  <FlexCol className="break-words text-[#637381]" style={{ width: "8%" }}>
                    {item.type}
                  </FlexCol>
                  <FlexCol className="break-words text-[#637381] justify-center" style={{ width: "13%" }}>
                    {item.status.id === 3 ? (
                      <span className="flex w-4 border-b border-[#637381] translate-y-1"></span>
                    ) : (
                      item.sentForApproval
                    )}
                  </FlexCol>
                  <FlexCol className="break-words text-[#637381]" style={{ width: "11%" }}>
                    <ApprovalListItem items={item.approversList} />
                  </FlexCol>
                </FlexRow>
              );
            })
          ) : (
            <EmptyList title="Здесь будут храниться ваши черновики" />
          )}
        </FlexCol>
      </FlexCol>
    </FlexRow>
  );
}

function TableHeaderColumn({
  tableHeaderContent,
  sortDirection,
  onClick,
  onStatusClickIcon,
  sortStatus,
}: TableHeaderColumnProps) {
  function handleIcon() {
    onStatusClickIcon && onStatusClickIcon();
  }

  return (
    <FlexCol className="select-none" style={tableHeaderContent.style}>
      <FlexRow className="gap-x-2 items-center">
        {tableHeaderContent.icon && (
          <FlexCol className={twMerge(sortStatus && sortStatus > 0 ? "active-svg" : "")} onClick={handleIcon}>
            {tableHeaderContent.icon}
          </FlexCol>
        )}
        <FlexCol className="font-medium whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}

function getStatus(status: number): JSX.Element {
  switch (status) {
    case 1:
      return <DraftIcon />;
    case 2:
      return <OnRevisionIcon />;
    case 3:
      return <OnReviewIcon />;
    case 4:
      return <RejectedIcon />;
    case 5:
      return <OnApprovalIcon />;
    default:
      return <DraftIcon />;
  }
}

function StatusListItem({ status }: { status: { title: CodeStatus; id: number } }) {
  return (
    <div className="flex items-center">
      {status ? (
        <TooltipItem position="top-right" tooltipsText={status.title}>
          {getStatus(status.id)}
        </TooltipItem>
      ) : (
        <DraftIcon />
      )}
    </div>
  );
}

const approvalItemStyle = cva("", {
  variants: {
    status: {
      pending: "ring-white ring-1",
      fulfilled: "ring-[#22AD5C] ring-2",
      reject: "ring-[#BA0C2F] ring-2",
    },
  },
  defaultVariants: {
    status: "pending",
  },
});

function ApprovalListItem({ items }: { items: ApproverList[] }) {
  function getStatus(status: number): "fulfilled" | "reject" | "pending" {
    switch (status) {
      case 13: // Согласован
        return "fulfilled";
      case 4: // Отклонен
      case 2: // на доработу
        return "reject";
      default:
        return "pending";
    }
  }

  return (
    <div className="flex -space-x-1">
      {items.map((item, i) => (
        <UserAvatarItem
          item={item}
          classNameText="text-[10px]"
          classImg={twMerge(approvalItemStyle({ status: getStatus(item.approved.id) }))}
        />
      ))}
    </div>
  );
}
